import type {
  ICodeSegmentsProps,
  ICodeSegmentsViewModel,
} from "@/components/screens/Login/Types/LoginTypes";
import {
  normaliseCodeInput,
  overwriteCodeAt,
  removeCodeCharAt,
} from "@/components/screens/Login/Utils/CodeUtils";
import { useRef } from "react";
import type { ClipboardEvent, FocusEvent, KeyboardEvent } from "react";

const CodeSegmentsViewModel = ({
  value,
  onChange,
  length,
  groupSize,
  onSubmit,
}: ICodeSegmentsProps): ICodeSegmentsViewModel => {
  const cellRefs = useRef<Array<HTMLInputElement | null>>([]);

  const cells = Array.from({ length }, (_, index) => value[index] ?? "");

  const focusCell = (index: number) => {
    const clamped = Math.max(0, Math.min(index, length - 1));
    const cell = cellRefs.current[clamped];
    cell?.focus();
    cell?.select();
  };

  const writeChars = (index: number, raw: string) => {
    const chars = normaliseCodeInput(raw);
    if (!chars) {
      return;
    }
    const next = overwriteCodeAt(value, index, chars);
    onChange(next);
    focusCell(Math.min(index + chars.length, next.length));
  };

  const onCellChange = (index: number, rawValue: string) => {
    writeChars(index, rawValue);
  };

  const onCellKeyDown = (
    index: number,
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      if (cells[index]) {
        onChange(removeCodeCharAt(value, index));
        focusCell(index);
      } else if (index > 0) {
        onChange(removeCodeCharAt(value, index - 1));
        focusCell(index - 1);
      }
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      focusCell(index - 1);
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      focusCell(index + 1);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      onSubmit();
    }
  };

  const onCellPaste = (
    index: number,
    event: ClipboardEvent<HTMLInputElement>,
  ) => {
    event.preventDefault();
    writeChars(index, event.clipboardData.getData("text"));
  };

  const onCellFocus = (event: FocusEvent<HTMLInputElement>) => {
    event.currentTarget.select();
  };

  const hasSeparatorAfter = (index: number): boolean =>
    (index + 1) % groupSize === 0 && index < length - 1;

  return {
    cellRefs,
    cells,
    onCellChange,
    onCellKeyDown,
    onCellPaste,
    onCellFocus,
    hasSeparatorAfter,
  };
};

export default CodeSegmentsViewModel;
